import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  contentChildren,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import {outputToObservable, takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

import {uniqId} from '@matchsource/utils';
import {RadioButtonComponent} from '../radio-button/radio-button.component';

enum RadioView {
  Standard = 'standard',
  Switch = 'switch',
}

type RadioViewLiteral = `${RadioView}`;

const RADIO_VIEW_TO_CLASS_MAP: Record<RadioViewLiteral, string> = {
  [RadioView.Standard]: '',
  [RadioView.Switch]: 'outline',
};

@Component({
  selector: 'ms-radio-group',
  templateUrl: './radio-group.component.html',
  styleUrls: ['./radio-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioGroupComponent),
      multi: true,
    },
  ],
  host: {
    '[class]': 'hostClass()',
  },
})
export class RadioGroupComponent implements ControlValueAccessor {
  readonly name = input(uniqId('radiogroup_'));
  readonly theme = input<`${RadioView}`>('switch');

  readonly options = contentChildren(
    forwardRef(() => RadioButtonComponent),
    {descendants: true}
  );

  readonly hostClass = computed(() => RADIO_VIEW_TO_CLASS_MAP[this.theme()]);

  value: unknown = null;
  disabled = false;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (val: unknown) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor() {
    effect(() => {
      this.bindOptions(this.options());
      this.syncChildrenSelection();
    });
  }

  private bindOptions(list: readonly RadioButtonComponent[]): void {
    list.forEach(opt => opt.detachFromGroup?.());

    list.forEach(opt => {
      opt.attachToGroup(this, this.name());
      outputToObservable(opt.groupSelectionChange$)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((selectedValue: unknown | null) => {
          this.setValueFromChild(selectedValue);
        });
    });

    this.syncChildrenState();
  }

  private setValueFromChild(selectedValue: unknown | null): void {
    const next = selectedValue;
    this.value = next;
    this.onChange(next);
    this.onTouched();
    this.syncChildrenSelection();
    this.cdr.markForCheck();
  }

  private syncChildrenSelection(): void {
    this.options()?.forEach(opt => {
      opt.setSelected(opt.value() === this.value);
    });
    this.cdr.markForCheck();
  }

  private syncChildrenState(): void {
    this.options()?.forEach(opt => {
      opt.setName(this.name());
      opt.setDisabled(this.disabled);
      opt.setSelected(opt.value() === this.value);
    });
    this.cdr.markForCheck();
  }

  writeValue(obj: unknown): void {
    this.value = obj ?? null;
    this.syncChildrenSelection();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (val: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.syncChildrenState();
    this.cdr.markForCheck();
  }
}
